import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { handleImageUpload, removeImage, createImagePreviews } from '../utils/imageUtils';
import { Section, Divider } from './common/SharedStyles';
import { SPACING } from './common/constants';
import {
  renderProductIntro,
  renderRatingSection,
  renderSkinInfoSection,
  renderReviewContentSection,
  renderTitleSection,
  renderImageUploadSection,
  renderRecommendationSection,
  renderNicknameSection,
  renderTermsSection,
  renderSubmitSection,
} from './common/formHelpers';
import type { MobileFormProps } from './common/interfaces';

// Mobile-specific styled components that differ from PC
const MobileSection = styled(Section)`
  margin-bottom: ${SPACING['2xl']};
`;

const MobileDivider = styled(Divider)`
  margin: ${SPACING.xl} 0;
`;

export const ReviewWriteMobileForm = ({
  rating,
  setRating,
  title,
  setTitle,
  content,
  setContent,
  nickname,
  setNickname,
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  images,
  setImages,
  productImage,
  recommend,
  setRecommend,
  isSubmitDisabled,
  onSubmit,
  skinTypeOptions,
  skinConcernOptions,
  isVerified,
  setIsVerified,
  productId,
}: MobileFormProps) => {
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e, images, setImages);
  };

  const handleRemoveImage = (idx: number) => {
    removeImage(idx, images, setImages);
  };

  // File[] -> string[] (preview url) conversion
  const imagePreviews = useMemo(() => createImagePreviews(images), [images]);

  return (
    <>
      {renderProductIntro({ productImage })}
      <MobileDivider />
      {renderRatingSection({ rating, setRating, SectionComponent: MobileSection })}
      {renderSkinInfoSection({
        skinType,
        setSkinType,
        skinConcern,
        setSkinConcern,
        skinTypeOptions,
        skinConcernOptions,
        SectionComponent: MobileSection,
      })}
      {renderReviewContentSection({ content, setContent, SectionComponent: MobileSection })}
      {renderTitleSection({ title, setTitle, SectionComponent: MobileSection })}
      {renderImageUploadSection({
        images,
        imagePreviews,
        handleImageChange,
        handleRemoveImage,
        SectionComponent: MobileSection,
      })}
      {renderRecommendationSection({ recommend, setRecommend, SectionComponent: MobileSection })}
      {renderNicknameSection({ nickname, setNickname, SectionComponent: MobileSection })}
      {renderTermsSection({ SectionComponent: MobileSection })}
      {renderSubmitSection({ isSubmitDisabled, onSubmit, productId })}
    </>
  );
};
